import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Coins, Crown, Sparkles, ArrowUpRight, ArrowDownLeft, Heart, ShieldCheck, Info } from "lucide-react";
import Seo from "@/components/Seo";

const TX_META = {
  earn_journal: { label: "Journal reflection", icon: Sparkles, tone: "earn" },
  earn_welcome: { label: "Welcome bonus", icon: Crown, tone: "earn" },
  earn_assessment: { label: "Assessment passed", icon: Crown, tone: "earn" },
  earn_track_complete: { label: "Track complete", icon: Crown, tone: "earn" },
  tip_sent: { label: "Tip sent", icon: ArrowUpRight, tone: "out" },
  tip_received: { label: "Wisdom was rewarded", icon: ArrowDownLeft, tone: "in" },
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return ""; }
};

export default function Wallet() {
  const { user, refresh } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [savingToggle, setSavingToggle] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/wallet");
      setWallet(data);
    } catch (err) {
      toast.error("Couldn't load your Wallet.");
    }
  };

  useEffect(() => { load(); }, []);

  const isStaff = user && user.role !== "participant" && user.role !== "admin";

  const toggleTips = async (next) => {
    setSavingToggle(true);
    try {
      await api.put("/me/settings", { crown_tips_enabled: next });
      await refresh();
      await load();
      toast.success(next ? "Tips re-enabled." : "You won't receive Wisdom tips.");
    } catch (err) {
      toast.error("Couldn't update that setting.");
    } finally {
      setSavingToggle(false);
    }
  };

  if (!wallet) {
    return (
      <div className="bg-tcpi-bg min-h-screen flex items-center justify-center text-[#8A8782]">
        Loading your Wallet…
      </div>
    );
  }

  if (isStaff || wallet.eligible === false) {
    return (
      <div className="bg-tcpi-bg min-h-screen" data-testid="wallet-page">
        <Seo title="Wallet · TCPI" />
        <section className="max-w-3xl mx-auto px-6 md:px-12 py-16">
          <h1 className="text-4xl text-[#F7F5F0] mb-4" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            Wallet
          </h1>
          <div className="card-bento" data-testid="wallet-staff-notice">
            <p className="text-[#D1CFC9] leading-relaxed">
              The $CROWN Wallet is reserved for participants. As a {user?.role || "staff member"},
              you don't hold a balance — this preserves the integrity of peer-to-peer Wisdom tipping.
            </p>
            <p className="text-xs text-[#8A8782] mt-4">{wallet.disclaimer}</p>
          </div>
        </section>
      </div>
    );
  }

  const { balance, transactions = [], top_giver, daily_tip_remaining, daily_tip_cap, tips_enabled, disclaimer } = wallet;

  return (
    <div className="bg-tcpi-bg min-h-screen" data-testid="wallet-page">
      <Seo title="Wallet · TCPI" />
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-16">
        <div className="mb-10">
          <div className="overline text-xs text-[#C9A961] mb-2">$CROWN</div>
          <h1 className="text-4xl md:text-5xl text-[#F7F5F0]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            Wallet
          </h1>
          <p className="text-[#8A8782] mt-2 text-sm max-w-2xl">
            Earn $CROWN by showing up — journaling, passing assessments, completing tracks.
            Tip a fellow participant whose words rewarded you with wisdom.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          <div className="card-bento md:col-span-2" data-testid="wallet-balance-card">
            <div className="flex items-center gap-2 text-[#C9A961] mb-3">
              <Coins className="w-4 h-4" />
              <span className="overline text-[10px]">Current balance</span>
            </div>
            <div className="text-6xl text-[#F7F5F0] tracking-tight" style={{ fontFamily: "Cormorant Garamond, serif" }} data-testid="wallet-balance-value">
              {balance.toLocaleString()}
              <span className="text-[#C9A961] text-2xl ml-3">$CROWN</span>
            </div>
            <div className="text-xs text-[#8A8782] mt-4 flex items-center gap-2">
              <ShieldCheck className="w-3 h-3" />
              {daily_tip_remaining} of {daily_tip_cap} $CROWN left to tip out today
            </div>
          </div>

          <div className="card-bento" data-testid="wallet-top-giver-card">
            <div className="flex items-center gap-2 text-[#C9A961] mb-3">
              <Heart className="w-4 h-4" />
              <span className="overline text-[10px]">Top Wisdom Giver</span>
            </div>
            {top_giver ? (
              <div>
                <div className="text-2xl text-[#F7F5F0]" style={{ fontFamily: "Cormorant Garamond, serif" }} data-testid="wallet-top-giver-name">
                  {top_giver.name}
                </div>
                <div className="text-xs text-[#8A8782] mt-1">
                  has rewarded your wisdom with{" "}
                  <span className="text-[#C9A961] font-medium">{top_giver.amount} $CROWN</span>
                </div>
              </div>
            ) : (
              <div className="text-[#8A8782] italic" data-testid="wallet-top-giver-empty">
                Waiting for wisdom to be shared.
              </div>
            )}
          </div>
        </div>

        <div className="card-bento mb-8" data-testid="wallet-tips-toggle-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[#F7F5F0] mb-1">Allow other participants to send you Wisdom tips</div>
              <div className="text-xs text-[#8A8782] max-w-xl">
                When this is off, no one can tip you. Your existing balance is unaffected.
                You can change this at any time.
              </div>
            </div>
            <button
              onClick={() => toggleTips(!tips_enabled)}
              disabled={savingToggle}
              data-testid="wallet-tips-toggle-btn"
              className={`px-4 py-2 text-sm rounded-md transition border ${
                tips_enabled
                  ? "border-[#C9A961] text-[#C9A961]"
                  : "border-[#8A8782] text-[#8A8782]"
              }`}
            >
              {tips_enabled ? "Tips ON" : "Tips OFF"}
            </button>
          </div>
        </div>

        <h2 className="text-2xl text-[#F7F5F0] mb-4" style={{ fontFamily: "Cormorant Garamond, serif" }}>
          Ledger
        </h2>
        <div className="card-bento" data-testid="wallet-ledger">
          {transactions.length === 0 ? (
            <div className="text-[#8A8782] italic py-6 text-center" data-testid="wallet-ledger-empty">
              No $CROWN activity yet. Submit a journal reflection to begin.
            </div>
          ) : (
            <ul className="divide-y divide-[#C9A961]/10">
              {transactions.map((t) => {
                const meta = TX_META[t.type] || { label: t.type, icon: Coins, tone: "earn" };
                const Icon = meta.icon;
                const sign = meta.tone === "out" ? "-" : "+";
                const color = meta.tone === "out" ? "text-[#E58A8A]" : meta.tone === "in" ? "text-[#A6E2A6]" : "text-[#C9A961]";
                return (
                  <li key={t.id} className="py-3 flex items-center gap-3" data-testid={`wallet-tx-${t.id}`}>
                    <div className="w-9 h-9 rounded-full bg-[#1A1816] border border-[#C9A961]/20 flex items-center justify-center">
                      <Icon className={`w-4 h-4 ${color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#F7F5F0] truncate">{t.description || meta.label}</div>
                      <div className="text-[11px] text-[#8A8782]">{fmtDate(t.created_at)}</div>
                    </div>
                    <div className={`text-sm font-medium ${color}`}>
                      {sign}{t.amount} $CROWN
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="mt-8 text-xs text-[#8A8782] flex items-start gap-2 max-w-2xl" data-testid="wallet-disclaimer">
          <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <p>{disclaimer}</p>
        </div>
      </section>
    </div>
  );
}
